import { Measurement } from "@cur8/rich-entity";
import { MetricConfig } from "./measurementConfig";

// Scan Result Handbook, updated 2026-08-26 by Yuan Tan. Only the handbook rules
// that hold unconditionally are here, and its two columns are merged: a reading
// a doctor must be told about also warrants the operator noticing.
//
// Not implemented:
//   - "Systolic >=130 / Diastolic >=85 (if no previous HTN/WCS)" needs member
//     history, which ConciergeUI does not have. These belong in the API risk
//     ranges Tom is updating.
//   - Escalate-to-a-doctor as its own louder tier.
//   - "difference between scans", which compares against a previous visit.
//   - ABI, saturation, eye pressure, eye side difference.

type Bounds = { min?: number; max?: number };

const BRACHIAL_BOUNDS: Record<string, Bounds> = {
  systolic: { min: 90, max: 180 },
  diastolic: { min: 50, max: 120 },
};

// The handbook sets no upper bound for ankle pressure.
const ANKLE_BOUNDS: Record<string, Bounds> = {
  value: { min: 70 },
};

const HANDBOOK_BOUNDS: Record<string, Record<string, Bounds>> = {
  "cardio.brachial_pressure.left": BRACHIAL_BOUNDS,
  "cardio.brachial_pressure.right": BRACHIAL_BOUNDS,
  "cardio.ankle_pressure.left": ANKLE_BOUNDS,
  "cardio.ankle_pressure.right": ANKLE_BOUNDS,
};

// The two carry different certainty and must not be worded the same way:
// "impossible" is arithmetic, "out-of-bounds" might still be the real value.
export type Implausibility =
  | { kind: "impossible" }
  | { kind: "out-of-bounds"; direction: "low" | "high" };

export function getImplausibility(
  config: MetricConfig,
  measurement: Measurement | undefined
): Implausibility | undefined {
  if (!measurement) {
    return undefined;
  }
  const bounds = HANDBOOK_BOUNDS[config.metricName];
  if (!bounds) {
    return undefined;
  }

  const dimension = (name: string) =>
    measurement.dimensions.find((d) => d.name === name)?.value;

  // Before the bounds: 210/340 fails both, and "impossible" says more.
  const systolic = dimension("systolic");
  const diastolic = dimension("diastolic");
  if (systolic != null && diastolic != null && diastolic >= systolic) {
    return { kind: "impossible" };
  }

  for (const [dimensionName, bound] of Object.entries(bounds)) {
    const value = dimension(dimensionName);
    if (value == null) {
      continue;
    }
    if (bound.min != null && value < bound.min) {
      return { kind: "out-of-bounds", direction: "low" };
    }
    if (bound.max != null && value > bound.max) {
      return { kind: "out-of-bounds", direction: "high" };
    }
  }

  return undefined;
}

export function isImplausibleReading(
  config: MetricConfig,
  measurement: Measurement | undefined
): boolean {
  return getImplausibility(config, measurement) != null;
}

export function isDiastolicAboveSystolic(
  systolic: number | null | undefined,
  diastolic: number | null | undefined
): boolean {
  if (systolic == null || diastolic == null) {
    return false;
  }
  if (isNaN(systolic) || isNaN(diastolic)) {
    return false;
  }
  return diastolic >= systolic;
}
